import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import Layout from "@/components/Layout";
import { apiGet, apiPost, apiPatch, apiDelete } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { toast } from "sonner";
import {
  Users, Plus, Search, Tag, KanbanSquare, Table2, Trash2,
  Sparkles, Send, X, Filter, MessageSquare, Flame, ChevronDown, Copy, Check,
  ExternalLink, Clock, CheckSquare, Square, ArrowRightCircle, XCircle,
  Download, Upload,
} from "lucide-react";
import OwlbertCard from "@/components/OwlbertCard";

/**
 * Outreach CRM (v1).
 *
 * Layout:
 *   Toolbar row  → search + label chips + stage filter + view switcher + New contact
 *   Body         → Kanban (default) or Table view of contacts
 *   Right panel  → contact drawer (thread, AI draft, template picker) — mounted on selection
 */
const VIEW_KEY = "ll_outreach_view";

/** Very small RFC-4180 compatible CSV parser (headers required). */
function parseCsv(text) {
  const rows = [];
  let cur = [];
  let field = "";
  let inQ = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQ) {
      if (ch === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; }
        else inQ = false;
      } else field += ch;
    } else {
      if (ch === '"') inQ = true;
      else if (ch === ",") { cur.push(field); field = ""; }
      else if (ch === "\n") { cur.push(field); rows.push(cur); cur = []; field = ""; }
      else if (ch === "\r") { /* skip */ }
      else field += ch;
    }
  }
  if (field.length > 0 || cur.length > 0) { cur.push(field); rows.push(cur); }
  if (rows.length < 2) return [];
  const headers = rows[0].map((h) => h.trim().toLowerCase().replace(/\s+/g, "_"));
  return rows.slice(1)
    .filter((r) => r.some((c) => c && c.trim()))
    .map((r) => Object.fromEntries(headers.map((h, idx) => [h, (r[idx] || "").trim()])));
}

export default function Outreach() {
  const { workspaceId } = useParams();
  const base = `/workspaces/${workspaceId}/outreach`;

  const [view, setView] = useState(() => localStorage.getItem(VIEW_KEY) || "kanban");
  const [contacts, setContacts] = useState([]);
  const [stages, setStages] = useState([]);
  const [labels, setLabels] = useState([]);
  const [q, setQ] = useState("");
  const [labelFilter, setLabelFilter] = useState("");
  const [stageFilter, setStageFilter] = useState("");
  const [staleDays, setStaleDays] = useState(0); // 0 = off
  const [selectedContact, setSelectedContact] = useState(null);
  const [labelDialogOpen, setLabelDialogOpen] = useState(false);
  const [stageDialogOpen, setStageDialogOpen] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [templates, setTemplates] = useState([]);

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const toggleSelect = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);
  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);
  const selectAll = useCallback((ids) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const id of ids) next.add(id);
      return next;
    });
  }, []);
  const deselectAll = useCallback((ids) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const id of ids) next.delete(id);
      return next;
    });
  }, []);

  useEffect(() => { localStorage.setItem(VIEW_KEY, view); }, [view]);

  const reload = useCallback(async () => {
    try {
      const params = {
        q: q || undefined,
        stage: stageFilter || undefined,
        label: labelFilter || undefined,
        stale_days: staleDays || undefined,
      };
      const [cs, ss, ls, ts] = await Promise.all([
        apiGet(`${base}/contacts`, params),
        apiGet(`${base}/stages`),
        apiGet(`${base}/labels`),
        apiGet(`${base}/templates`),
      ]);
      setContacts(Array.isArray(cs) ? cs : []);
      setStages(Array.isArray(ss) ? ss : []);
      setLabels(Array.isArray(ls) ? ls : []);
      setTemplates(Array.isArray(ts) ? ts : []);
    } catch (e) {
      toast.error("could not load outreach");
    }
  }, [base, q, stageFilter, labelFilter, staleDays]);

  useEffect(() => { reload(); }, [reload]);

  const grouped = useMemo(() => {
    const out = {};
    for (const s of stages) out[s.name] = [];
    for (const c of contacts) {
      const k = c.stage || (stages[0]?.name);
      if (k && out[k]) out[k].push(c);
      else if (stages[0]) out[stages[0].name].push(c);
    }
    return out;
  }, [contacts, stages]);

  const staleCount = useMemo(() => {
    if (!staleDays) return 0;
    return contacts.length;
  }, [contacts, staleDays]);

  // Actions ------------------------------------------------------------
  const patchContact = async (contactId, patch) => {
    try {
      const updated = await apiPatch(`${base}/contacts/${contactId}`, patch);
      setContacts((prev) => prev.map((c) => (c.id === contactId ? { ...c, ...updated } : c)));
      if (selectedContact?.id === contactId) setSelectedContact((s) => ({ ...s, ...updated }));
    } catch {
      toast.error("update failed");
    }
  };

  const moveStage = (contactId, newStage) => patchContact(contactId, { stage: newStage });

  // Bulk actions ------------------------------------------------------
  const bulkAction = async (payload) => {
    const contact_ids = Array.from(selectedIds);
    if (contact_ids.length === 0) return;
    try {
      const r = await apiPost(`${base}/contacts/bulk`, { contact_ids, ...payload });
      const changed = r?.updated ?? r?.deleted ?? contact_ids.length;
      toast.success(`${changed} contact${changed === 1 ? "" : "s"} updated`);
      clearSelection();
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "bulk action failed");
    }
  };
  const bulkMoveStage = (stage) => bulkAction({ action: "stage", stage });
  const bulkAddLabel = (label_id) => bulkAction({ action: "add_label", label_id });
  const bulkClose = () =>
    bulkAction({ action: "close", outcome: "closed in bulk" });
  const bulkDelete = () => {
    if (!window.confirm(`Delete ${selectedIds.size} contact${selectedIds.size === 1 ? "" : "s"}? This cannot be undone.`)) return;
    bulkAction({ action: "delete" });
  };

  // CSV export — client-side, no backend call needed.
  const csvEscape = (v) => {
    const s = v == null ? "" : String(v);
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const exportSelectedCsv = () => {
    const ids = selectedIds.size > 0 ? selectedIds : new Set(contacts.map((c) => c.id));
    const rows = contacts.filter((c) => ids.has(c.id));
    if (rows.length === 0) return;
    const labelMap = Object.fromEntries(labels.map((l) => [l.label_id, l.name]));
    const header = [
      "name", "headline", "linkedin_url", "stage", "labels",
      "reason_targeted", "reason_for_outreach", "recommended_next_message",
      "last_activity_at",
    ];
    const body = rows.map((c) => [
      c.name, c.headline, c.linkedin_url, c.stage,
      (c.labels || []).map((id) => labelMap[id] || id).join(", "),
      c.reason_targeted, c.reason_for_outreach, c.recommended_next_message,
      (c.last_activity_at || "").slice(0, 10),
    ].map(csvEscape).join(","));
    const csv = [header.join(","), ...body].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `outreach-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} contact${rows.length === 1 ? "" : "s"}`);
  };

  // CSV import
  const importCsv = async (file) => {
    if (!file) return;
    try {
      const text = await file.text();
      const rows = parseCsv(text);
      if (rows.length === 0) {
        toast.error("no rows found in CSV");
        return;
      }
      const r = await apiPost(`${base}/contacts/import`, { rows });
      toast.success(
        `Import done · ${r.imported} new · ${r.updated} updated · ${r.skipped} skipped`,
        { duration: 6000 }
      );
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "import failed");
    }
  };

  const openContact = async (contact) => {
    setSelectedContact(contact);
  };

  return (
    <Layout title="Outreach">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap" data-testid="outreach-toolbar">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-stone" />
            <Input
              placeholder="Search contacts…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9 w-[240px]"
              data-testid="outreach-search"
            />
          </div>
          <StageFilter
            stages={stages}
            value={stageFilter}
            onChange={setStageFilter}
          />
          <LabelFilter
            labels={labels}
            value={labelFilter}
            onChange={setLabelFilter}
          />
          <button
            onClick={() => setStaleDays(staleDays ? 0 : 14)}
            className={`text-xs px-3 py-1.5 rounded-md border transition-colors inline-flex items-center gap-1.5 ${
              staleDays
                ? "border-amber-700 bg-amber-500/10 text-amber-300"
                : "border-hairline bg-graphite text-stone hover:text-parchment"
            }`}
            data-testid="outreach-stale-toggle"
          >
            <Clock className="w-3 h-3" /> Stale 14d+{staleDays ? ` (${staleCount})` : ""}
          </button>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setLabelDialogOpen(true)}
            className="text-xs text-stone hover:text-parchment inline-flex items-center gap-1"
            data-testid="outreach-manage-labels"
          >
            <Tag className="w-3 h-3" /> labels
          </button>
          <button
            onClick={() => setStageDialogOpen(true)}
            className="text-xs text-stone hover:text-parchment inline-flex items-center gap-1"
            data-testid="outreach-manage-stages"
          >
            <KanbanSquare className="w-3 h-3" /> stages
          </button>
          <button
            onClick={() => setTemplateDialogOpen(true)}
            className="text-xs text-stone hover:text-parchment inline-flex items-center gap-1"
            data-testid="outreach-manage-templates"
          >
            <MessageSquare className="w-3 h-3" /> templates
          </button>
          <label
            className="text-xs text-stone hover:text-parchment inline-flex items-center gap-1 cursor-pointer"
            data-testid="outreach-import-csv-label"
            title="Import contacts from CSV. Header row: name,headline,linkedin_url,stage,labels,reason_targeted,reason_for_outreach,recommended_next_message"
          >
            <Upload className="w-3 h-3" /> import
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; importCsv(f); }}
              className="hidden"
              data-testid="outreach-import-csv-input"
            />
          </label>
          <button
            onClick={exportSelectedCsv}
            className="text-xs text-stone hover:text-parchment inline-flex items-center gap-1"
            data-testid="outreach-export-csv"
            title="Export current view (or selected) to CSV"
          >
            <Download className="w-3 h-3" /> export
          </button>
          <div className="inline-flex items-center bg-graphite border border-hairline rounded-md p-1" data-testid="outreach-view-switch">
            {["kanban", "table"].map((k) => (
              <button
                key={k}
                onClick={() => setView(k)}
                data-testid={`outreach-view-${k}`}
                className={`text-xs font-medium px-3 py-1.5 rounded-sm capitalize inline-flex items-center gap-1.5 transition-colors ${
                  view === k ? "bg-graphite-2 text-terracotta" : "text-stone hover:text-parchment"
                }`}
              >
                {k === "kanban" ? <KanbanSquare className="w-3 h-3" /> : <Table2 className="w-3 h-3" />}
                {k}
              </button>
            ))}
          </div>
          <NewContactButton
            onCreated={(c) => { setContacts((prev) => [c, ...prev]); }}
            workspaceId={workspaceId}
          />
        </div>
      </div>

      {/* Body */}
      {contacts.length === 0 ? (
        <EmptyStateOutreach />
      ) : view === "kanban" ? (
        <OutreachKanban
          stages={stages}
          grouped={grouped}
          labels={labels}
          onOpen={openContact}
          onMove={moveStage}
          selectedIds={selectedIds}
          onToggleSelect={toggleSelect}
          onSelectAllInColumn={selectAll}
          onDeselectAllInColumn={deselectAll}
        />
      ) : (
        <OutreachTable
          contacts={contacts}
          labels={labels}
          onOpen={openContact}
          selectedIds={selectedIds}
          onToggleSelect={toggleSelect}
          onSelectAll={selectAll}
          onDeselectAll={deselectAll}
        />
      )}

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <BulkActionBar
          count={selectedIds.size}
          stages={stages}
          labels={labels}
          onMoveStage={bulkMoveStage}
          onAddLabel={bulkAddLabel}
          onExport={exportSelectedCsv}
          onClose={bulkClose}
          onDelete={bulkDelete}
          onClear={clearSelection}
        />
      )}

      {/* Contact drawer */}
      {selectedContact && (
        <ContactDrawer
          contact={selectedContact}
          workspaceId={workspaceId}
          stages={stages}
          labels={labels}
          templates={templates}
          onClose={() => setSelectedContact(null)}
          onPatch={(patch) => patchContact(selectedContact.id, patch)}
          onReload={reload}
        />
      )}

      {/* Manager dialogs */}
      {labelDialogOpen && (
        <LabelManager
          workspaceId={workspaceId}
          labels={labels}
          onClose={() => setLabelDialogOpen(false)}
          onChanged={reload}
        />
      )}
      {stageDialogOpen && (
        <StageManager
          workspaceId={workspaceId}
          stages={stages}
          onClose={() => setStageDialogOpen(false)}
          onChanged={reload}
        />
      )}
      {templateDialogOpen && (
        <TemplateManager
          workspaceId={workspaceId}
          templates={templates}
          onClose={() => setTemplateDialogOpen(false)}
          onChanged={reload}
        />
      )}
    </Layout>
  );
}

// ---------------------------------------------------------------------------
// Toolbar bits
// ---------------------------------------------------------------------------
function StageFilter({ stages, value, onChange }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="text-xs px-3 py-1.5 bg-graphite border border-hairline rounded-md text-parchment"
      data-testid="outreach-stage-filter"
    >
      <option value="">All stages</option>
      {stages.map((s) => (
        <option key={s.stage_id} value={s.name}>{s.name}</option>
      ))}
    </select>
  );
}

function LabelFilter({ labels, value, onChange }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="text-xs px-3 py-1.5 bg-graphite border border-hairline rounded-md text-parchment"
      data-testid="outreach-label-filter"
    >
      <option value="">All labels</option>
      {labels.map((l) => (
        <option key={l.label_id} value={l.label_id}>{l.name}</option>
      ))}
    </select>
  );
}

function NewContactButton({ onCreated, workspaceId }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [headline, setHeadline] = useState("");
  const [linkedinUrl, setLinkedinUrl] = useState("");
  const [reason, setReason] = useState("");

  const save = async () => {
    if (!name.trim()) return toast.error("name is required");
    try {
      const c = await apiPost(`/workspaces/${workspaceId}/contacts`, {
        name: name.trim(),
        headline: headline.trim() || undefined,
        linkedin_url: linkedinUrl.trim() || undefined,
        relationship: "prospect",
      });
      // Patch reason via outreach patch endpoint if provided
      if (reason.trim()) {
        await apiPatch(`/workspaces/${workspaceId}/outreach/contacts/${c.id}`, {
          reason_targeted: reason.trim(),
        });
        c.reason_targeted = reason.trim();
      }
      onCreated(c);
      setOpen(false);
      setName(""); setHeadline(""); setLinkedinUrl(""); setReason("");
      toast.success("contact added");
    } catch {
      toast.error("could not add contact");
    }
  };

  return (
    <>
      <Button
        size="sm"
        onClick={() => setOpen(true)}
        data-testid="outreach-new-contact"
        className="inline-flex items-center gap-1.5"
      >
        <Plus className="w-3.5 h-3.5" /> New contact
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="new-contact-dialog">
          <DialogHeader>
            <DialogTitle>New contact</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} data-testid="new-contact-name" />
            <Input placeholder="Headline" value={headline} onChange={(e) => setHeadline(e.target.value)} data-testid="new-contact-headline" />
            <Input placeholder="LinkedIn URL" value={linkedinUrl} onChange={(e) => setLinkedinUrl(e.target.value)} data-testid="new-contact-url" />
            <Textarea
              placeholder="Reason targeted (why they are on the list)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              data-testid="new-contact-reason"
            />
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>Cancel</Button>
              <Button size="sm" onClick={save} data-testid="new-contact-save">Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ---------------------------------------------------------------------------
// Views: kanban + table
// ---------------------------------------------------------------------------
function OutreachKanban({ stages, grouped, labels, onOpen, onMove, selectedIds, onToggleSelect, onSelectAllInColumn, onDeselectAllInColumn }) {
  const [dragging, setDragging] = useState(null);
  return (
    <div className="grid gap-3 overflow-x-auto pb-4" style={{ gridTemplateColumns: `repeat(${Math.max(stages.length, 1)}, minmax(240px, 1fr))` }} data-testid="outreach-kanban">
      {stages.map((s) => {
        const rows = grouped[s.name] || [];
        const rowIds = rows.map((r) => r.id);
        const allSelected = rowIds.length > 0 && rowIds.every((id) => selectedIds.has(id));
        const someSelected = rowIds.some((id) => selectedIds.has(id));
        return (
          <div
            key={s.stage_id}
            className="surface p-3 flex flex-col min-h-[300px]"
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => {
              if (dragging) onMove(dragging, s.name);
              setDragging(null);
            }}
            data-testid={`kanban-col-${s.name.replace(/\s+/g, "-").toLowerCase()}`}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                {rows.length > 0 && (
                  <button
                    type="button"
                    onClick={() => (allSelected ? onDeselectAllInColumn(rowIds) : onSelectAllInColumn(rowIds))}
                    className={`shrink-0 flex items-center justify-center w-4 h-4 rounded transition-colors ${
                      allSelected || someSelected ? "text-terracotta" : "text-stone-dim hover:text-parchment"
                    }`}
                    data-testid={`kanban-select-all-${s.name.replace(/\s+/g, "-").toLowerCase()}`}
                    title={allSelected ? "Deselect all in column" : "Select all in column"}
                  >
                    {allSelected ? <CheckSquare className="w-4 h-4" strokeWidth={2} /> : <Square className={`w-4 h-4 ${someSelected ? "text-terracotta" : ""}`} strokeWidth={2} />}
                  </button>
                )}
                <span className="text-xs font-bold uppercase tracking-wider text-parchment">{s.name}</span>
              </div>
              <span className="mono text-[10px] text-stone">{rows.length}</span>
            </div>
            <div className="space-y-2 flex-1 min-h-[80px]">
              {rows.map((c) => {
                const isSel = selectedIds.has(c.id);
                return (
                  <div
                    key={c.id}
                    draggable
                    onDragStart={() => setDragging(c.id)}
                    onClick={(e) => {
                      // If a checkbox click or the user is in "select mode", toggle selection.
                      if (selectedIds.size > 0) {
                        onToggleSelect(c.id);
                      } else {
                        onOpen(c);
                      }
                    }}
                    className={`surface p-3 cursor-pointer hover:border-hairline-hover transition-colors ${isSel ? "ring-2 ring-terracotta" : ""}`}
                    data-testid={`contact-card-${c.id}`}
                  >
                    <div className="flex items-start gap-2">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onToggleSelect(c.id); }}
                        className={`shrink-0 mt-0.5 flex items-center justify-center w-4 h-4 rounded transition-colors ${
                          isSel ? "text-terracotta" : "text-stone-dim hover:text-parchment"
                        }`}
                        data-testid={`select-contact-${c.id}`}
                        aria-label={isSel ? "Deselect contact" : "Select contact"}
                      >
                        {isSel ? <CheckSquare className="w-4 h-4" strokeWidth={2} /> : <Square className="w-4 h-4" strokeWidth={2} />}
                      </button>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-parchment font-semibold truncate">{c.name}</div>
                        {c.headline && <div className="text-[11px] text-stone truncate">{c.headline}</div>}
                        <ContactLabels contactLabels={c.labels || []} allLabels={labels} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function OutreachTable({ contacts, labels, onOpen, selectedIds, onToggleSelect, onSelectAll, onDeselectAll }) {
  const allIds = contacts.map((c) => c.id);
  const allSelected = allIds.length > 0 && allIds.every((id) => selectedIds.has(id));
  const someSelected = allIds.some((id) => selectedIds.has(id));
  return (
    <div className="surface overflow-hidden" data-testid="outreach-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[10px] mono uppercase tracking-wider text-stone bg-graphite">
            <th className="pl-4 pr-2 py-2 w-8">
              <button
                type="button"
                onClick={() => (allSelected ? onDeselectAll(allIds) : onSelectAll(allIds))}
                className={`flex items-center justify-center w-4 h-4 rounded ${
                  allSelected || someSelected ? "text-terracotta" : "text-stone-dim hover:text-parchment"
                }`}
                data-testid="table-select-all"
                title={allSelected ? "Deselect all" : "Select all"}
              >
                {allSelected ? <CheckSquare className="w-4 h-4" strokeWidth={2} /> : <Square className={`w-4 h-4 ${someSelected ? "text-terracotta" : ""}`} strokeWidth={2} />}
              </button>
            </th>
            <th className="px-4 py-2 font-medium">Name</th>
            <th className="px-3 py-2 font-medium">Stage</th>
            <th className="px-3 py-2 font-medium">Labels</th>
            <th className="px-3 py-2 font-medium">Reason</th>
            <th className="px-3 py-2 font-medium">Last activity</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-hairline">
          {contacts.map((c) => {
            const isSel = selectedIds.has(c.id);
            return (
              <tr
                key={c.id}
                onClick={() => {
                  if (selectedIds.size > 0) onToggleSelect(c.id);
                  else onOpen(c);
                }}
                className={`hover:bg-graphite/40 cursor-pointer transition-colors ${isSel ? "bg-terracotta/5" : ""}`}
                data-testid={`table-row-${c.id}`}
              >
                <td className="pl-4 pr-2 py-3 w-8" onClick={(e) => e.stopPropagation()}>
                  <button
                    type="button"
                    onClick={() => onToggleSelect(c.id)}
                    className={`flex items-center justify-center w-4 h-4 rounded ${
                      isSel ? "text-terracotta" : "text-stone-dim hover:text-parchment"
                    }`}
                    data-testid={`select-row-${c.id}`}
                    aria-label={isSel ? "Deselect" : "Select"}
                  >
                    {isSel ? <CheckSquare className="w-4 h-4" strokeWidth={2} /> : <Square className="w-4 h-4" strokeWidth={2} />}
                  </button>
                </td>
                <td className="px-4 py-3">
                  <div className="text-parchment font-semibold">{c.name}</div>
                  <div className="text-[11px] text-stone">{c.headline || "—"}</div>
                </td>
                <td className="px-3 py-3 text-parchment text-xs">{c.stage || "—"}</td>
                <td className="px-3 py-3"><ContactLabels contactLabels={c.labels || []} allLabels={labels} /></td>
                <td className="px-3 py-3 text-stone text-xs max-w-[280px] truncate">{c.reason_targeted || "—"}</td>
                <td className="px-3 py-3 mono text-[11px] text-stone">{(c.last_activity_at || "").slice(0, 10) || "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function BulkActionBar({ count, stages, labels, onMoveStage, onAddLabel, onExport, onClose, onDelete, onClear }) {
  const [stageMenuOpen, setStageMenuOpen] = useState(false);
  const [labelMenuOpen, setLabelMenuOpen] = useState(false);
  return (
    <div
      className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-5 py-3 bg-white rounded-full shadow-lg"
      style={{ boxShadow: "0 12px 40px rgba(17,24,39,0.14), 0 4px 12px rgba(17,24,39,0.08)" }}
      data-testid="outreach-bulk-bar"
    >
      <span className="text-sm font-semibold text-[#111827]" data-testid="outreach-bulk-count">
        {count} selected
      </span>
      <span className="w-px h-5 bg-[#e5e7eb]" />

      <Popover open={stageMenuOpen} onOpenChange={setStageMenuOpen}>
        <PopoverTrigger asChild>
          <button
            className="text-xs font-semibold text-[#4b5563] hover:text-[#111827] inline-flex items-center gap-1.5"
            data-testid="outreach-bulk-move-stage"
          >
            <ArrowRightCircle className="w-3.5 h-3.5" /> Move to stage <ChevronDown className="w-3 h-3" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="center" className="w-56 p-2 bg-white shadow-lg rounded-xl border-0">
          {stages.map((s) => (
            <button
              key={s.stage_id}
              onClick={() => { setStageMenuOpen(false); onMoveStage(s.name); }}
              className="w-full text-left text-sm text-[#111827] px-3 py-2 rounded-lg hover:bg-[#f3f4f6]"
              data-testid={`outreach-bulk-stage-${s.name.replace(/\s+/g, "-").toLowerCase()}`}
            >
              {s.name}
            </button>
          ))}
        </PopoverContent>
      </Popover>

      <Popover open={labelMenuOpen} onOpenChange={setLabelMenuOpen}>
        <PopoverTrigger asChild>
          <button
            className="text-xs font-semibold text-[#4b5563] hover:text-[#111827] inline-flex items-center gap-1.5"
            data-testid="outreach-bulk-add-label"
          >
            <Tag className="w-3.5 h-3.5" /> Add label <ChevronDown className="w-3 h-3" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="center" className="w-56 p-2 bg-white shadow-lg rounded-xl border-0">
          {labels.length === 0 && <div className="text-xs text-[#9ca3af] px-3 py-2">No labels yet.</div>}
          {labels.map((l) => (
            <button
              key={l.label_id}
              onClick={() => { setLabelMenuOpen(false); onAddLabel(l.label_id); }}
              className="w-full text-left text-sm text-[#111827] px-3 py-2 rounded-lg hover:bg-[#f3f4f6] inline-flex items-center gap-2"
              data-testid={`outreach-bulk-label-${l.label_id}`}
            >
              <span
                className="w-2.5 h-2.5 rounded-full shrink-0"
                style={{ background: l.color || "#4f46e5" }}
              />
              {l.name}
            </button>
          ))}
        </PopoverContent>
      </Popover>

      <button
        onClick={onExport}
        className="text-xs font-semibold text-[#4b5563] hover:text-[#111827] inline-flex items-center gap-1.5"
        data-testid="outreach-bulk-export"
      >
        <Download className="w-3.5 h-3.5" /> Export CSV
      </button>
      <button
        onClick={onClose}
        className="text-xs font-semibold text-[#4b5563] hover:text-[#111827] inline-flex items-center gap-1.5"
        data-testid="outreach-bulk-close"
      >
        <XCircle className="w-3.5 h-3.5" /> Close
      </button>
      <button
        onClick={onDelete}
        className="text-xs font-semibold text-[#ef4444] hover:text-[#b91c1c] inline-flex items-center gap-1.5"
        data-testid="outreach-bulk-delete"
      >
        <Trash2 className="w-3.5 h-3.5" /> Delete
      </button>

      <span className="w-px h-5 bg-[#e5e7eb]" />

      <button
        onClick={onClear}
        className="text-xs text-[#9ca3af] hover:text-[#111827] inline-flex items-center gap-1.5"
        data-testid="outreach-bulk-clear"
        title="Clear selection"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

function ContactLabels({ contactLabels, allLabels }) {
  const items = (contactLabels || [])
    .map((id) => allLabels.find((l) => l.label_id === id))
    .filter(Boolean);
  if (items.length === 0) return null;
  return (
    <div className="flex items-center gap-1 mt-2 flex-wrap">
      {items.map((l) => (
        <span
          key={l.label_id}
          className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded"
          style={{ background: `${l.color}22`, color: l.color, border: `1px solid ${l.color}44` }}
        >
          {l.name}
        </span>
      ))}
    </div>
  );
}

function EmptyStateOutreach() {
  return (
    <div className="surface p-12 text-center max-w-xl mx-auto" data-testid="outreach-empty">
      <Users className="w-8 h-8 text-stone mx-auto mb-3" strokeWidth={1.5} />
      <h3 className="text-lg font-semibold text-parchment mb-1">No contacts yet.</h3>
      <p className="text-sm text-stone">Add your first prospect to start the pipeline.</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Contact drawer
// ---------------------------------------------------------------------------
function ContactDrawer({ contact, workspaceId, stages, labels, templates, onClose, onPatch, onReload }) {
  const base = `/workspaces/${workspaceId}/outreach`;
  const [thread, setThread] = useState([]);
  const [draft, setDraft] = useState(contact.recommended_next_message || "");
  const [intent, setIntent] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [drafting, setDrafting] = useState(false);
  const [copied, setCopied] = useState(false);

  const loadThread = useCallback(async () => {
    try {
      const rows = await apiGet(`${base}/contacts/${contact.id}/messages`);
      setThread(Array.isArray(rows) ? rows : []);
    } catch { setThread([]); }
  }, [base, contact.id]);

  useEffect(() => { loadThread(); }, [loadThread]);
  useEffect(() => { setDraft(contact.recommended_next_message || ""); }, [contact.id, contact.recommended_next_message]);

  const generateDraft = async () => {
    setDrafting(true);
    try {
      const res = await apiPost(`${base}/draft-next-message`, {
        contact_id: contact.id,
        intent: intent || undefined,
        template_id: selectedTemplate || undefined,
      });
      setDraft(res.draft || "");
      onPatch({ recommended_next_message: res.draft || "" });
      toast.success("draft ready");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "draft failed");
    } finally {
      setDrafting(false);
    }
  };

  const logMessage = async (direction) => {
    if (!draft.trim()) return toast.error("message is empty");
    try {
      await apiPost(`${base}/contacts/${contact.id}/messages`, {
        body: draft.trim(),
        direction,
      });
      setDraft("");
      onPatch({ recommended_next_message: "" });
      await loadThread();
      toast.success(direction === "in" ? "reply logged" : "message logged");
    } catch { toast.error("log failed"); }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(draft); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch (_e) { /* ignore clipboard failures */ }
  };

  const closeContact = async () => {
    const outcome = window.prompt("Outcome (short summary):", contact.closed_outcome || "");
    if (!outcome) return;
    try {
      await apiPost(`${base}/contacts/${contact.id}/close`, { outcome });
      toast.success("closed");
      onReload();
      onClose();
    } catch { toast.error("close failed"); }
  };

  const toggleLabel = (labelId) => {
    const cur = new Set(contact.labels || []);
    if (cur.has(labelId)) cur.delete(labelId); else cur.add(labelId);
    onPatch({ labels: [...cur] });
  };

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm" onClick={onClose} data-testid="drawer-backdrop" />
      <aside
        className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none"
        role="dialog"
        aria-modal="true"
      >
        <div
          className="pointer-events-auto w-full max-w-3xl max-h-[92vh] bg-obsidian border border-hairline rounded-2xl shadow-2xl flex flex-col overflow-hidden"
          data-testid="contact-drawer"
          onClick={(e) => e.stopPropagation()}
        >
        <header className="flex items-start justify-between px-6 py-4 border-b border-hairline">
          <div className="min-w-0">
            <h3 className="text-xl font-bold text-parchment truncate" data-testid="drawer-name">{contact.name}</h3>
            {contact.headline && <p className="text-xs text-stone truncate">{contact.headline}</p>}
            {contact.linkedin_url && (
              <a href={contact.linkedin_url} target="_blank" rel="noopener noreferrer"
                 className="mono text-[11px] text-terracotta hover:underline inline-flex items-center gap-1 mt-1"
                 data-testid="drawer-linkedin">
                open on LinkedIn <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
          <button onClick={onClose} className="p-2 text-stone hover:text-parchment" aria-label="Close" data-testid="drawer-close">
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
          {/* Stage + labels */}
          <div className="flex items-center gap-3 flex-wrap">
            <select
              value={contact.stage || ""}
              onChange={(e) => onPatch({ stage: e.target.value })}
              className="text-xs px-3 py-1.5 bg-graphite border border-hairline rounded-md text-parchment"
              data-testid="drawer-stage"
            >
              {stages.map((s) => <option key={s.stage_id} value={s.name}>{s.name}</option>)}
            </select>
            <Popover>
              <PopoverTrigger asChild>
                <button className="text-xs px-3 py-1.5 bg-graphite border border-hairline rounded-md text-stone hover:text-parchment inline-flex items-center gap-1" data-testid="drawer-labels-btn">
                  <Tag className="w-3 h-3" /> Labels <ChevronDown className="w-3 h-3" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="bg-obsidian border-hairline text-parchment w-56 p-1" data-testid="drawer-labels-popover">
                {labels.length === 0 && <div className="text-xs text-stone p-2">No labels yet — create some from the toolbar.</div>}
                {labels.map((l) => {
                  const on = (contact.labels || []).includes(l.label_id);
                  return (
                    <button
                      key={l.label_id}
                      onClick={() => toggleLabel(l.label_id)}
                      className="w-full text-left text-xs px-2 py-1.5 hover:bg-graphite rounded inline-flex items-center gap-2"
                      data-testid={`drawer-toggle-label-${l.label_id}`}
                    >
                      <span className="w-2.5 h-2.5 rounded-full" style={{ background: l.color }} />
                      <span className="flex-1 truncate">{l.name}</span>
                      {on && <Check className="w-3 h-3 text-terracotta" />}
                    </button>
                  );
                })}
              </PopoverContent>
            </Popover>
            <ContactLabels contactLabels={contact.labels || []} allLabels={labels} />
          </div>

          {/* Client-facing fields */}
          <FieldBlock label="Reason targeted" value={contact.reason_targeted} onSave={(v) => onPatch({ reason_targeted: v })} testid="drawer-reason-targeted" />
          <FieldBlock label="Reason for outreach" value={contact.reason_for_outreach} onSave={(v) => onPatch({ reason_for_outreach: v })} testid="drawer-reason-outreach" />
          <FieldBlock label="Notes (internal)" value={contact.notes} onSave={(v) => onPatch({ notes: v })} testid="drawer-notes" />

          {/* Thread */}
          <div>
            <div className="overline text-[11px] text-stone mb-2">Thread ({thread.length})</div>
            {thread.length === 0 ? (
              <div className="text-xs text-stone italic">No messages yet.</div>
            ) : (
              <div className="space-y-2">
                {thread.map((m) => (
                  <div key={m.message_id}
                    className={`text-xs p-2.5 rounded border ${
                      m.direction === "in"
                        ? "bg-emerald-500/5 border-emerald-800/40"
                        : m.direction === "note"
                          ? "bg-graphite border-hairline"
                          : "bg-terracotta/5 border-terracotta/30"
                    }`}
                    data-testid={`thread-msg-${m.message_id}`}
                  >
                    <div className="mono text-[10px] uppercase text-stone mb-1">
                      {m.direction === "in" ? "them" : m.direction === "note" ? "note" : "you"} · {(m.sent_at || "").slice(0, 10)}
                    </div>
                    <div className="text-parchment whitespace-pre-wrap">{m.body}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* AI Draft */}
          <div>
            <div className="overline text-[11px] text-stone mb-2">Recommended next message</div>
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <Input placeholder="Intent (optional): 'follow up', 'book a call'…" value={intent} onChange={(e) => setIntent(e.target.value)} className="flex-1 min-w-[200px]" data-testid="drawer-intent" />
              {templates.length > 0 && (
                <select value={selectedTemplate} onChange={(e) => setSelectedTemplate(e.target.value)} className="text-xs px-3 py-1.5 bg-graphite border border-hairline rounded-md text-parchment" data-testid="drawer-template">
                  <option value="">No template</option>
                  {templates.map((t) => <option key={t.template_id} value={t.template_id}>{t.name}</option>)}
                </select>
              )}
              <Button size="sm" onClick={generateDraft} disabled={drafting} className="inline-flex items-center gap-1.5" data-testid="drawer-generate">
                <Sparkles className={`w-3.5 h-3.5 ${drafting ? "animate-pulse" : ""}`} />
                {drafting ? "Drafting…" : "AI draft"}
              </Button>
            </div>
            <Textarea
              rows={5}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="The AI draft will appear here — you can edit before saving/sending."
              data-testid="drawer-draft"
            />
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <Button size="sm" onClick={copy} variant="ghost" className="inline-flex items-center gap-1.5" data-testid="drawer-copy">
                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                {copied ? "Copied" : "Copy"}
              </Button>
              <Button size="sm" onClick={() => logMessage("out")} className="inline-flex items-center gap-1.5" data-testid="drawer-log-sent">
                <Send className="w-3.5 h-3.5" /> Log as sent
              </Button>
              <Button size="sm" variant="outline" onClick={() => logMessage("in")} data-testid="drawer-log-reply">
                Log their reply
              </Button>
              <div className="flex-1" />
              <button onClick={closeContact} className="text-xs text-rose-400 hover:text-rose-300 inline-flex items-center gap-1" data-testid="drawer-close-contact">
                <Trash2 className="w-3 h-3" /> Close contact
              </button>
            </div>
          </div>

          {/* Owlbert quick chat */}
          <div>
            <div className="overline text-[11px] text-stone mb-2">Ask Owlbert about this contact</div>
            <OwlbertCard workspaceId={workspaceId} workspaceName={contact.name} />
          </div>
        </div>
        </div>
      </aside>
    </>
  );
}

function FieldBlock({ label, value, onSave, testid }) {
  const [editing, setEditing] = useState(false);
  const [buf, setBuf] = useState(value || "");
  useEffect(() => { setBuf(value || ""); }, [value]);
  return (
    <div>
      <div className="overline text-[11px] text-stone mb-1">{label}</div>
      {editing ? (
        <div className="space-y-2">
          <Textarea rows={2} value={buf} onChange={(e) => setBuf(e.target.value)} data-testid={`${testid}-input`} />
          <div className="flex gap-2">
            <Button size="sm" onClick={() => { onSave(buf.trim()); setEditing(false); }} data-testid={`${testid}-save`}>Save</Button>
            <Button size="sm" variant="ghost" onClick={() => { setBuf(value || ""); setEditing(false); }}>Cancel</Button>
          </div>
        </div>
      ) : (
        <div onClick={() => setEditing(true)} className="text-sm text-parchment whitespace-pre-wrap cursor-text min-h-[24px] p-2 rounded border border-transparent hover:border-hairline" data-testid={testid}>
          {value || <span className="text-stone italic">click to add…</span>}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Manager dialogs
// ---------------------------------------------------------------------------
function LabelManager({ workspaceId, labels, onClose, onChanged }) {
  const [name, setName] = useState("");
  const [color, setColor] = useState("#6B7280");
  const base = `/workspaces/${workspaceId}/outreach`;
  const add = async () => {
    if (!name.trim()) return;
    try { await apiPost(`${base}/labels`, { name: name.trim(), color }); setName(""); onChanged(); }
    catch { toast.error("add failed"); }
  };
  const remove = async (id) => {
    try { await apiDelete(`${base}/labels/${id}`); onChanged(); } catch { toast.error("delete failed"); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="label-manager">
        <DialogHeader><DialogTitle>Labels</DialogTitle></DialogHeader>
        <div className="space-y-2 max-h-[280px] overflow-auto">
          {labels.map((l) => (
            <div key={l.label_id} className="flex items-center gap-2 p-2 rounded border border-hairline">
              <span className="w-3 h-3 rounded-full" style={{ background: l.color }} />
              <span className="flex-1 text-parchment text-sm">{l.name}</span>
              <button onClick={() => remove(l.label_id)} className="text-stone hover:text-rose-400" aria-label="Delete label" data-testid={`label-delete-${l.label_id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2 mt-3">
          <input type="color" value={color} onChange={(e) => setColor(e.target.value)} className="w-10 h-9 rounded" data-testid="label-color" />
          <Input placeholder="Label name" value={name} onChange={(e) => setName(e.target.value)} data-testid="label-name" />
          <Button size="sm" onClick={add} data-testid="label-add">Add</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function StageManager({ workspaceId, stages, onClose, onChanged }) {
  const [name, setName] = useState("");
  const base = `/workspaces/${workspaceId}/outreach`;
  const add = async () => {
    if (!name.trim()) return;
    try { await apiPost(`${base}/stages`, { name: name.trim() }); setName(""); onChanged(); }
    catch { toast.error("add failed"); }
  };
  const rename = async (id, newName) => {
    if (!newName?.trim()) return;
    try { await apiPatch(`${base}/stages/${id}`, { name: newName.trim() }); onChanged(); }
    catch { toast.error("rename failed"); }
  };
  const remove = async (id) => {
    try { await apiDelete(`${base}/stages/${id}`); onChanged(); } catch { toast.error("delete failed"); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="stage-manager">
        <DialogHeader><DialogTitle>Pipeline stages</DialogTitle></DialogHeader>
        <div className="space-y-2 max-h-[320px] overflow-auto">
          {stages.map((s) => (
            <div key={s.stage_id} className="flex items-center gap-2 p-2 rounded border border-hairline">
              <Input
                defaultValue={s.name}
                onBlur={(e) => e.target.value.trim() !== s.name && rename(s.stage_id, e.target.value)}
                className="flex-1"
                data-testid={`stage-input-${s.stage_id}`}
              />
              <button onClick={() => remove(s.stage_id)} className="text-stone hover:text-rose-400" aria-label="Delete stage" data-testid={`stage-delete-${s.stage_id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2 mt-3">
          <Input placeholder="Stage name" value={name} onChange={(e) => setName(e.target.value)} data-testid="stage-name" />
          <Button size="sm" onClick={add} data-testid="stage-add">Add</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TemplateManager({ workspaceId, templates, onClose, onChanged }) {
  const [name, setName] = useState("");
  const [body, setBody] = useState("");
  const base = `/workspaces/${workspaceId}/outreach`;
  const add = async () => {
    if (!name.trim() || !body.trim()) return;
    try { await apiPost(`${base}/templates`, { name: name.trim(), body }); setName(""); setBody(""); onChanged(); }
    catch { toast.error("add failed"); }
  };
  const remove = async (id) => {
    try { await apiDelete(`${base}/templates/${id}`); onChanged(); } catch { toast.error("delete failed"); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="template-manager" className="max-w-[560px]">
        <DialogHeader><DialogTitle>Message templates</DialogTitle></DialogHeader>
        <div className="space-y-2 max-h-[320px] overflow-auto">
          {templates.map((t) => (
            <div key={t.template_id} className="p-3 rounded border border-hairline space-y-1">
              <div className="flex items-center gap-2">
                <span className="text-sm text-parchment font-semibold flex-1">{t.name}</span>
                <button onClick={() => remove(t.template_id)} className="text-stone hover:text-rose-400" aria-label="Delete template" data-testid={`tpl-delete-${t.template_id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="text-xs text-stone whitespace-pre-wrap">{t.body}</div>
            </div>
          ))}
        </div>
        <div className="space-y-2 mt-3">
          <Input placeholder="Template name" value={name} onChange={(e) => setName(e.target.value)} data-testid="tpl-name" />
          <Textarea placeholder="Template body" rows={4} value={body} onChange={(e) => setBody(e.target.value)} data-testid="tpl-body" />
          <Button size="sm" onClick={add} data-testid="tpl-add">Add template</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
